import React, { useState, useContext, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePreviousProjectStep } from '../../../hooks/usePreviousProjectStep';
import TrainNewModelButton from './TrainNewModelButton';
import { ProjectContext } from '../../../layouts/CreateProjectLayout';
import axios from 'axios';
import ModalTestModel from '../../ModelZoo/ModalTestModel';
import ModelFrameworkBox from '../../ModelZoo/ModelFrameworkBox';
import ModelTaskBox from '../../ModelZoo/ModelTaskBox';
import TrainingStatusBox from '../../ModelZoo/TrainingStatusBox';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExportTrainingToModelZooButton from '../../ModelZoo/ExportTrainingToModelZooButton';
import { useEdition } from '../../../edition/EditionContext';
import { useNextProjectStep } from '../../../hooks/useNextProjectStep';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
const TrainingsStep = () => {
  const navigate = useNavigate();
  const {
    projectTrainings,
    loadingTrainings,
    trainingsError,
    fetchProjectTrainings,
    canEditProject,
  } = useContext(ProjectContext);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [starredModels, setStarredModels] = useState([]);

  const toggleStar = (modelId) => {
    setStarredModels(prev =>
      prev.includes(modelId) ? prev.filter(id => id !== modelId) : [...prev, modelId]
    );
  };

  const [selectedImage, setSelectedImage] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [openTestModelDialog, setOpenTestModelDialog] = useState(null);

  const { goToPreviousStep } = usePreviousProjectStep();
  const { goToNextStep } = useNextProjectStep();

  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [latestTrainingStatus, setLatestTrainingStatus] = useState(null);
  const [pollIntervalId, setPollIntervalId] = useState(null);

  const { isDemo } = useEdition();

  // ✅ Helpers: metrics
  const getMetric = (training, key) =>
    training?.model?.info?.metrics?.[key] ??
    training?.status_msg?.metrics?.[key] ??
    null;

  const getMetricsObject = (training) =>
    training?.model?.info?.metrics ??
    training?.status_msg?.metrics ??
    null;

  const fmt = (v, digits = 3) =>
    typeof v === 'number' ? v.toFixed(digits) : '-';

  // ✅ We now want mAP50-95 for the table's metrics column + sorting
  const getMap5095 = (training) => getMetric(training, 'metrics/mAP50-95(B)');

  const handleNewTraining = async (trainingId) => {
    setLatestTrainingStatus(null); // reset
    await fetchProjectTrainings(); // refresh table immediately
    fetchTrainingStatus(trainingId);
  };

  const fetchTrainingStatus = async (trainingId) => {
    try {
      const res = await axios.get(`/trainings/${trainingId}`);
      setLatestTrainingStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch training status', error);
    }
  };

  useEffect(() => {
    if (!latestTrainingStatus) return;

    if (['completed', 'failed'].includes(latestTrainingStatus.status)) {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        setPollIntervalId(null);
      }
      return;
    }

    const id = setInterval(async () => {
      try {
        const res = await axios.get(`/trainings/${latestTrainingStatus.id}`);
        setLatestTrainingStatus(res.data);
        await fetchProjectTrainings(); // update the table with latest progress
      } catch (error) {
        console.error('Polling training status failed', error);
      }
    }, 5000);

    setPollIntervalId(id);

    return () => {
      clearInterval(id);
    };
  }, [latestTrainingStatus]); // keeping your dependency style as-is

  const handleNext = () => {
    goToNextStep();
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date);
  };

  const handleOpenTestModelDialog = (index) => {
    setOpenTestModelDialog(index);
  };

  const handleCloseTestModelDialog = () => {
    setOpenTestModelDialog(null);
    setSelectedImage(null);
    setPreviewURL(null);
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const formatMetricKey = (key) => {
  return key
    .replace('metrics/', '')
    .replace(/_/g, ' ')
    .replace(/\(B\)/g, ' (Box)')
    .replace(/\(M\)/g, ' (Mask)');
};

const formatMetricValue = (value) => {
  if (typeof value === 'number') {
    return value.toFixed(3);
  }
  return value;
};

  const handleOpenClearML = (clearmlUrl) => {
    window.open(clearmlUrl, '_blank');
  }

  // const sortedTrainings = [...projectTrainings].sort((a, b) => {
  //   return new Date(b.created_at) - new Date(a.created_at);
  // });

  let sortedTrainings = [...projectTrainings];

  if (sortConfig.key) {
    sortedTrainings.sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'star':
          aVal = starredModels.includes(a.id) ? 1 : 0;
          bVal = starredModels.includes(b.id) ? 1 : 0;
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;

        case 'name':
          aVal = a.info?.name?.toLowerCase() || '';
          bVal = b.info?.name?.toLowerCase() || '';
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);

        case 'metrics': {
          // ✅ sort by mAP50-95
          aVal = getMap5095(a);
          bVal = getMap5095(b);

          // Put trainings without metric at the bottom (both asc/desc)
          const aHas = aVal != null;
          const bHas = bVal != null;
          if (!aHas && !bHas) return 0;
          if (!aHas) return 1;
          if (!bHas) return -1;

          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        case 'date':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;

        case 'task':
          aVal = a.info?.training_params?.modelTask || '';
          bVal = b.info?.training_params?.modelTask || '';
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);

        case 'framework':
          aVal = a.info?.training_params?.modelFramework || '';
          bVal = b.info?.training_params?.modelFramework || '';
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);

        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);

        default:
          return 0;
      }
    });
  } else {
    // default: sort by date descending
    sortedTrainings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  return (
    <Box sx={{ ml: 29.5, mt: 1, borderRadius: 4, overflow: 'hidden', width: { xs: '80%', sm: '90%' }, display: 'flex', flexDirection: 'column', p: 1 }}>
      <Typography variant="h3" fontWeight={700} color="black" mt={4}>
        Trainings
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 12, mt: 2 }}>
        <Typography variant="h6" color="rgb(158, 151, 151)">
          Train a new model or test an existing one from the list below.
        </Typography>
        <TrainNewModelButton onTrainingCreated={handleNewTraining} disabled={!canEditProject} />
      </Box>

      {console.log({
        latestTrainingStatus,
        loadingTrainings,
        projectTrainings,
      })}

      {!loadingTrainings && sortedTrainings.length === 0 && (
        <Typography mt={3} color="gray">
          No trained models found.
        </Typography>
      )}

      {!loadingTrainings && sortedTrainings.length > 0 && (
        <Box sx={{ width: '75%', display: 'flex', flexDirection: 'column', gap: 2, padding: 2 }}>
          <TableContainer sx={{ mt: 2, borderRadius: '12px', border: '1px solid #cccccc' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => handleSort('star')}
                  >
                    {sortConfig.key === 'star' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('date')}
                  >
                    Created At {sortConfig.key === 'date' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('task')}
                  >
                    Task {sortConfig.key === 'task' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('framework')}
                  >
                    Framework {sortConfig.key === 'framework' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    Status {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('metrics')}
                  >
                    mAP50-95 {sortConfig.key === 'metrics' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    Name {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedTrainings.map((training, index) => {
                  const isExpanded = expandedProjectId === index;
                  const downloadUrl = training.model ? (training.model.pt || training.model.onnx) : null;
                  const fileExtension = downloadUrl?.endsWith('.onnx') ? 'onnx' : 'pt';
                  const filename = training.model?.name ? training.model.name.replace(/\s+/g, '_') : 'model';

                  const map5095 = getMap5095(training);

                  return (
                    <React.Fragment key={training.id || index}>
                      <TableRow
                        onClick={() => {
                          if (training.status === 'completed') {
                            setExpandedProjectId(isExpanded ? null : index);
                          }
                        }}
                        sx={{
                          cursor: training.status === 'completed' ? 'pointer' : 'default',
                          '&:hover': training.status === 'completed'
                            ? { backgroundColor: '#f5f5f5' }
                            : undefined,
                          borderBottom: isExpanded ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                          '& td': { borderBottom: isExpanded ? 'none' : undefined },
                        }}
                      >
                        <TableCell
                          sx={{ textAlign: 'center', fontSize: '1.2rem', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(training.id);
                          }}
                        >
                          {starredModels.includes(training.id) ? '⭐' : '☆'}
                        </TableCell>

                        <TableCell sx={{ py: 1.3, fontSize: '1.1rem' }}>
                          {formatDate(training.created_at)}
                        </TableCell>

                        <TableCell sx={{ py: 1.3, fontSize: '1.0rem' }}>
                          <ModelTaskBox sx={{ ml: 1 }} type={training.info?.training_params?.modelTask || '-'}>
                            {training.info?.training_params?.modelTask || '-'}
                          </ModelTaskBox>
                        </TableCell>

                        <TableCell sx={{ py: 1.3, fontSize: '1.0rem' }}>
                          <ModelFrameworkBox sx={{ ml: 1 }} type={training.info?.training_params?.modelFramework || '-'}>
                            {training.info?.training_params?.modelFramework || '-'}
                          </ModelFrameworkBox>
                        </TableCell>

                        <TableCell sx={{ py: 1.3, fontSize: '1.0rem' }}>
                          <TrainingStatusBox type={training.status}>
                            {training.status}
                          </TrainingStatusBox>
                        </TableCell>

                        {/* ✅ show primary metric */}
                        <TableCell sx={{ py: 1.3, fontSize: '1.1rem' }}>
                          {(() => {
                            // Use training_params for framework — model.info.modelFramework is only set after zoo export
                            const fw = training.info?.training_params?.modelFramework || '';
                            // Prefer status_msg metrics (raw training output, never overwritten by zoo export)
                            const metrics = training.status_msg?.metrics ?? training.model?.info?.metrics ?? {};
                            const mAP50 = metrics['metrics/mAP50(B)'];
                            const mIoU = metrics['mean_iou_(val)'];
                            const f1 = metrics['image_F1Score'];
                            if (fw.includes('yolo') && mAP50 != null)
                              return <Typography variant="body2">{`mAP: ${Number(mAP50).toFixed(2)}`}</Typography>;
                            if (fw.includes('imagenet') && mIoU != null)
                              return <Typography variant="body2">{`mIoU: ${Number(mIoU).toFixed(2)}`}</Typography>;
                            if (fw.includes('anomalib') && f1 != null)
                              return <Typography variant="body2">{`F1: ${Number(f1).toFixed(2)}`}</Typography>;
                            if (map5095 != null)
                              return <Typography variant="body2">{Number(map5095).toFixed(2)}</Typography>;
                            return <Typography variant="body2">-</Typography>;
                          })()}
                        </TableCell>

                        <TableCell sx={{ py: 1.3, fontSize: '1.1rem', width: '20%' }}>
                          {training.info?.name}
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow sx={{ '& td': { borderTop: 'none' } }}>
                          <TableCell colSpan={7} sx={{ py: 3.5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',   // pushes left & right apart
                                alignItems: 'flex-start',          // top align everything
                                width: '100%',
                              }}
                            >
                            <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                              <Typography variant="h7" fontSize={16} fontWeight={600}>
                                Training metrics: 
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr', // two equal columns
                                columnGap: 4, // bigger gap between columns
                                rowGap: 1,    // smaller gap between rows
                                ml: 2,
                              }}
                            >
                              {(() => {
                                // Prefer status_msg.metrics (raw output); model.info.metrics may be overwritten by zoo export
                                const m = training.status_msg?.metrics ?? training.model?.info?.metrics;
                                return m ? (
                                  Object.entries(m).map(([key, value]) => (
                                    <Typography key={key} variant="body1">
                                      {formatMetricKey(key)}: {formatMetricValue(value)}
                                    </Typography>
                                  ))
                                ) : (
                                  <Typography variant="body2">-</Typography>
                                );
                              })()}
                            </Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 4,
                                alignItems: 'flex-start',
                              }}
                            >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                startIcon={<CloudQueueIcon />}
                                variant="text"
                              sx={{
                                // mt: 2,
                                // ml: 2.5,
                                textTransform: 'none',
                                borderRadius: 4,
                                color: '#16a4b0',
                                borderColor: '#16a4b0',
                                boxShadow: 'none',
                                alignSelf: 'flex-start',
                                fontSize: '16px',
                              }}
                              onClick={() => handleOpenClearML(training.status_msg?.clearml_task_url)}
                              disabled={!canEditProject}
                              >
                                View on ClearML
                              </Button>
                            <Button
                              startIcon={<DownloadIcon />}
                              variant="text"
                              component="a"
                              href={downloadUrl || '#'}
                              download={`${filename}.${fileExtension}`}
                              disabled={!downloadUrl || isDemo || !canEditProject}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 4,
                                color: '#16a4b0',
                                borderColor: '#16a4b0',
                                boxShadow: 'none',
                                alignSelf: 'flex-start',
                                fontSize: '16px',
                              }}
                            >
                              Download
                            </Button>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                              startIcon={<AutoFixHighIcon />}
                                variant="text"
                              sx={{
                                // mt: 2,
                                // ml: 2.5,
                                
                                textTransform: 'none',
                                borderRadius: 4,
                                color: '#16a4b0',
                                borderColor: '#16a4b0',
                                boxShadow: 'none',
                                alignSelf: 'flex-start',
                                fontSize: '16px',
                              }}
                                onClick={() => handleOpenTestModelDialog(index)}
                                disabled={!canEditProject}
                              >
                                Test
                              </Button>
                              {training.model && (
                                <ExportTrainingToModelZooButton model={training.model} variant="text" disabled={!canEditProject} />
                              )}
                            </Box>
                            </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Optional: show trainingsError if you want */}
          {trainingsError && (
            <Typography mt={2} color="red">
              {trainingsError}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ padding: 2, width: { xs: '80%', sm: '70%' }, display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
        <Button
          variant="outlined"
          onClick={goToPreviousStep}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '16px',
            width: '120px',
            color: '#16a4b0',
            borderColor: '#16a4b0',
            '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          sx={{
            borderRadius: '12px',
            backgroundColor: '#16a4b0',
            color: 'white',
            textTransform: 'none',
            fontSize: '16px',
            width: '120px',
            '&:hover': { backgroundColor: '#00707c' },
          }}
        >
          Next
        </Button>
      </Box>

      {openTestModelDialog !== null && (
        <ModalTestModel
          open={openTestModelDialog !== null}
          model={sortedTrainings[openTestModelDialog].model}
          onClose={handleCloseTestModelDialog}
          testImage={selectedImage}
          setTestImage={setSelectedImage}
          previewURL={previewURL}
          setPreviewURL={setPreviewURL}
          showTestImages={false}
        />
      )}
    </Box>
  );
};

export default TrainingsStep;
